import React, { useState, useEffect, useMemo } from 'react';
import './Team.css';
import { AiOutlineInfoCircle } from "react-icons/ai";

const Team = () => {
  const teamMembers = useMemo(() => [
      {
        name: 'Peter Gloor',
        role: 'Chief Creative Officer',
        affiliation: 'Prof Dr., University of Cologne and MIT',
        img: 'Team/Peter_Gloor.jpeg',
        linkedin: 'https://www.linkedin.com/in/petergloor/?originalSubdomain=ch',
      },
      {
        name: 'Moritz Weinbeer',
        role: 'Plant Biology',
        affiliation: 'Dr., Biology, BDAS',
        img: 'Team/Animation_Moritz.mp4',
        isVideo: true,
        infoLink: 'https://www.researchgate.net/scientific-contributions/Moritz-Weinbeer-77902837',
      },
      {
        name: 'Emily Kieson',
        role: 'Animal Psychology',
        affiliation: 'Dr., Equine International',
        img: 'Team/Emily Kieson.webp',
        linkedin: 'https://www.linkedin.com/in/emilykieson/',
      },
      {
        name: 'Joao Gabriel Marra',
        role: 'Software Development',
        affiliation: 'University Juiz de Fora',
        img: 'Team/Joao_Gabriel.jpeg',
        linkedin: 'https://www.linkedin.com/in/jo%C3%A3o-gabriel-silva-marra-b1ba4ba1/',
      },
      {
        name: 'Alina Hafner',
        role: 'Business Development',
        affiliation: 'PhD Student, TUM (Technical University Munich)',
        img: 'Team/Alina_Hafner.jpeg',
        linkedin: 'https://www.linkedin.com/in/alina-hafner/',
      },
      {
        name: 'Peter Bertschinger',
        role: 'Sales',
        affiliation: 'i-star',
        img: 'Team/Peter Bertschinger.jpg',
        linkedin: 'https://www.linkedin.com/in/peter-bertschinger-74564433/',
      }
  ], []);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(getItemsPerView());
  const [startX, setStartX] = useState(0); // Track initial touch X position
  const [endX, setEndX] = useState(0);   // Track final touch X position

  // Function to determine items per view based on screen size
  function getItemsPerView() {
    if (window.innerWidth >= 1024) return 3; // Desktop
    if (window.innerWidth >= 768) return 2;  // Tablet
    return 1;                                // Mobile
  }

  useEffect(() => {
    const handleResize = () => {
      const newItemsPerView = getItemsPerView();
      if (newItemsPerView !== itemsPerView) {
        setItemsPerView(newItemsPerView);
        const newTotalSlides = Math.ceil(teamMembers.length / newItemsPerView);
        if (currentSlide >= newTotalSlides) {
          setCurrentSlide(newTotalSlides - 1);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [itemsPerView, currentSlide, teamMembers.length]);

  const groupedMembers = useMemo(() => {
    const groups = [];
    for (let i = 0; i < teamMembers.length; i += itemsPerView) {
      groups.push(teamMembers.slice(i, i + itemsPerView));
    }
    return groups;
  }, [teamMembers, itemsPerView]);

  const totalSlides = groupedMembers.length;

  // Handle touch start (finger down)
  const handleTouchStart = (e) => {
    setStartX(e.touches[0].clientX); // Get the initial X position
  };

  // Handle touch move (finger moves on the screen)
  const handleTouchMove = (e) => {
    setEndX(e.touches[0].clientX); // Update the final X position
  };

  // Handle touch end (finger lifted off the screen)
  const handleTouchEnd = () => {
    const deltaX = startX - endX; // Calculate the swipe distance
    if (deltaX > 50) {
      // Swipe left (move to the next slide)
      setCurrentSlide((prev) => (prev < totalSlides - 1 ? prev + 1 : 0));
    } else if (deltaX < -50) {
      // Swipe right (move to the previous slide)
      setCurrentSlide((prev) => (prev > 0 ? prev - 1 : totalSlides - 1));
    }
  };

  return (
    <section id="team" className="team-section">
      <h2 className="team-title">Galaxy Team</h2>
      <p className="team-subtitle">Meet the incredible minds behind Galaxy Advisors AG.</p>

      <div
        className="slider-container"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="slider"
          style={{
            transform: `translateX(-${currentSlide * 100}%)`,
          }}
        >
          {groupedMembers.map((group, index) => (
            <div key={index} className="slider-slide">
              {group.map((member, idx) => (
                <div
                  key={idx}
                  className="slider-item"
                  style={{ flex: `0 0 ${100 / itemsPerView}%` }}
                >
                  <div className="team-card">
                    {member.isVideo ? (
                      <video
                        className="team-video"
                        src={member.img}
                        autoPlay
                        loop
                        muted
                        playsInline
                      />
                    ) : (
                      <img src={member.img} alt={member.name} className="team-img" />
                    )}
                    <h3 className="team-name">{member.name}</h3>
                    <p className="team-role">{member.role}</p>
                    <p className="team-affiliation">{member.affiliation}</p>

                    {/* Conditional Logic for Info or LinkedIn */}
                    <div className="info-container">
                      {member.infoLink ? (
                        <a href={member.infoLink} target="_blank" rel="noopener noreferrer">
                          <AiOutlineInfoCircle className="info-icon" />
                        </a>
                      ) : member.linkedin ? (
                        <a href={member.linkedin} target="_blank" rel="noopener noreferrer">
                          <img src="Icons/Linkedin_Team.png" alt="LinkedIn" className="linkedin-icon" />
                        </a>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="pagination">
        {Array.from({ length: totalSlides }).map((_, i) => (
          <span
            key={i}
            className={`dot ${i === currentSlide ? 'active' : ''}`}
            onClick={() => setCurrentSlide(i)}
          ></span>
        ))}
      </div>
    </section>
  );
};

export default Team;